import { css } from '@emotion/react';
import styled from '@emotion/styled';
import { Slot } from '@radix-ui/react-slot';

import { font } from '../Font/Font.styles';
import type {
  FormatsMap,
  SizesMap,
  StyleProps,
  VariantsMap,
} from './Button.types';

const variantsMap: VariantsMap = {
  important: {
    default: {
      backgroundColor:
        'var(--sembo-inspire-s-color-background-important-interactive)',
      color: 'var(--color-white)',
      borderColor: 'transparent',
      textDecoration: 'none',
    },
    hover: {
      backgroundColor:
        'var(--sembo-inspire-s-color-background-important-interactive-hover)',
    },
    focus: {
      borderColor: 'var(--color-white)',
    },
    disabled: {
      backgroundColor: 'var(--color-neutral-30)',
      color: 'var(--color-neutral-40)',
    },
  },
  primary: {
    default: {
      backgroundColor:
        'var(--sembo-inspire-s-color-background-primary-interactive)',
      color: 'var(--sembo-inspire-s-color-text-primary-interactive)',
      borderColor: 'transparent',
      textDecoration: 'none',
    },
    hover: {
      backgroundColor:
        'var(--sembo-inspire-s-color-background-primary-interactive-hover)',
    },
    focus: {
      borderColor: 'var(--color-white)',
    },
    disabled: {
      backgroundColor: 'var(--color-neutral-30)',
      color: 'var(--color-neutral-40)',
    },
  },
  secondary: {
    default: {
      backgroundColor: 'var(--color-white)',
      color: 'var(--color-black)',
      borderColor: 'var(--color-black)',
      textDecoration: 'none',
    },
    hover: {
      backgroundColor: 'var(--color-neutral-20)',
    },
    focus: {
      borderColor: 'transparent',
    },
    disabled: {
      backgroundColor: 'var(--color-neutral-30)',
      color: 'var(--color-neutral-40)',
    },
  },
  text: {
    default: {
      backgroundColor: 'transparent',
      color: 'var(--color-black)',
      borderColor: 'transparent',
      textDecoration: 'underline',
    },
    hover: {
      backgroundColor: 'var(--color-neutral-20)',
    },
    focus: {
      borderColor: 'transparent',
    },
    disabled: {
      backgroundColor: 'transparent',
      color: 'var(--color-neutral-40)',
    },
  },
};

const sizesMap: SizesMap = {
  small: {
    minHeight: '24px',
    fontVar: 'button-sm',
    paddingInline: 'var(--spacing-12)',
    iconWidth: '18px',
  },
  medium: {
    minHeight: '44px',
    fontVar: 'button-md',
    paddingInline: 'var(--spacing-24)',
    iconWidth: '24px',
  },
  large: {
    minHeight: '56px',
    fontVar: 'button-lg',
    paddingInline: 'var(--spacing-24)',
    iconWidth: '24px',
  },
};

const formatsMap: FormatsMap = {
  auto: {
    width: 'auto',
    display: 'inline-flex',
    aspectRatio: 'auto',
  },
  icon: {
    width: 'auto',
    display: 'inline-flex',
    aspectRatio: 1,
  },
  block: {
    width: '100%',
    display: 'flex',
    aspectRatio: 'auto',
  },
};

const commonStyles = ({ variant, size, format }: StyleProps) => css`
  ${font(sizesMap[size].fontVar)};
  width: ${formatsMap[format].width};
  min-height: ${sizesMap[size].minHeight};
  background-color: ${variantsMap[variant].default.backgroundColor};
  color: ${variantsMap[variant].default.color};
  text-decoration: ${variantsMap[variant].default.textDecoration};
  padding-inline: ${format === 'icon' ? '0' : sizesMap[size].paddingInline};
  ${format === 'icon'
    ? `width: ${sizesMap[size].minHeight}; height: ${sizesMap[size].minHeight};`
    : ''}
  aspect-ratio: ${formatsMap[format].aspectRatio};
  display: ${formatsMap[format].display};
  align-items: center;
  justify-content: center;
  gap: var(--spacing-4);
  border: solid 1px ${variantsMap[variant].default.borderColor};
  border-radius: var(--border-radius-m);
  white-space: nowrap;
  cursor: pointer;
  transition-property: background-color, transform;
  transition-duration: 0.3s;
  transition-timing-function: ease-out;

  &:hover {
    background-color: ${variantsMap[variant].hover.backgroundColor};
  }

  &:focus-visible {
    border-color: ${variantsMap[variant].focus.borderColor};
    outline: solid 2px var(--color-black);
  }

  &:not(:disabled):active {
    transform: matrix(0.95, 0, 0, 0.95, 0, 0);
  }

  svg {
    width: ${sizesMap[size].iconWidth};
    height: auto;
    color: inherit;
  }
`;

export const StyledButton = styled.button<StyleProps>`
  ${commonStyles}

  &:disabled {
    background-color: ${({ variant }) =>
      variantsMap[variant].disabled.backgroundColor};
    color: ${({ variant }) => variantsMap[variant].disabled.color};
    border-color: transparent;
    cursor: not-allowed;

    svg {
      color: ${({ variant }) => variantsMap[variant].disabled.color};
    }
  }
`;

export const StyledSlot = styled(Slot)<StyleProps>`
  ${commonStyles}

  span {
    text-decoration: inherit !important;
  }

  &:hover {
    color: ${({ variant }) => variantsMap[variant].default.color} !important;
  }
`;
